import "style/videoMetadataPage.css";
import { setScrollBehavior } from "features/scrollPageBehaviorProvider/scrollPageBehaviorProvider";
import { _ } from "i18n";
import client from "libs/client";
import { type CSSProperties, useEffect, useState } from "react";
import { Link } from "react-router";

const tableStyle = {
	width: "100%",
	borderCollapse: "collapse",
	marginTop: "20px",
	fontFamily: "Arial, sans-serif",
	fontSize: "16px",
} satisfies CSSProperties;

const thStyle = {
	backgroundColor: "#007BFF",
	color: "white",
	padding: "10px",
	border: "1px solid #ddd",
	textAlign: "left",
} satisfies CSSProperties;

const tdStyle = {
	padding: "10px",
	border: "1px solid #ddd",
} satisfies CSSProperties;

const captionStyle = {
	fontSize: "18px",
	fontWeight: "bold",
	marginBottom: "10px",
	textAlign: "left",
	color: "#333",
} satisfies CSSProperties;

const nestedTableStyle = {
	width: "100%",
	borderCollapse: "collapse",
} satisfies CSSProperties;

function workerSort(workerA: { id: string }, workerB: { id: string }) {
	return Number.parseInt(workerA.id) - Number.parseInt(workerB.id);
}

export function VideoUploadsServerStatusPage() {
	const { data, refetch } = client.getVideoServerStatus.useQuery();

	const [refreshedAt, setRefreshedAt] = useState(new Date)

	useEffect(() => {
		setScrollBehavior();
	}, []);

	const LinkToVideo = ({worker}: {worker: { videoId: string; name: string }}) => {
		return (
			<Link target="_blank" to={`/video-uploads/${worker.videoId}/status`}>
				{`${worker.name}(${worker.videoId})`}
			</Link>
		);
	};

	return (
		<div className="videoMetadataList-container">
			<div className="topControls">
				<div className="refetchButton" onClick={async () => {
					await refetch()
					setRefreshedAt(new Date)
				}}>
					<span className="icon-new icon-admin-syc icon-white icon-24" />
					<div className="buttonText">{_("label.refresh")}</div>
				</div>
				<div>Refreshed at: {refreshedAt.toLocaleDateString()} {refreshedAt.toLocaleTimeString()}</div>
			</div>

			<table style={tableStyle}>
				<caption style={captionStyle}>Processing Workers</caption>
				<tbody>
					{Object.entries(data?.data.workers?.["processing-workers"] || {}).map(([node, workers]) => (
						<tr key={node}>
							<td style={tdStyle}>
								<table style={nestedTableStyle}>
									<caption style={captionStyle}>{node}</caption>
									<thead>
										<tr>
											<th style={thStyle}>ID</th>
											<th style={thStyle}>Status</th>
											<th style={thStyle}>Name</th>
											<th style={thStyle}>Last Update</th>
										</tr>
									</thead>
									<tbody>
										{workers.sort(workerSort).map((worker) => (
											<tr key={worker.id}>
												<td style={tdStyle}>{worker.id}</td>
												<td style={tdStyle}>{worker.status}</td>
												<td style={tdStyle}>
													{worker.status === "busy" ? (
														<LinkToVideo worker={worker} />
													) : (
														""
													)}
												</td>
												<td style={tdStyle}>{new Date(worker.lastUpdate).toLocaleString()}</td>
											</tr>
										))}
									</tbody>
								</table>
							</td>
						</tr>
					))}
				</tbody>
			</table>

			<table style={tableStyle}>
				<caption style={captionStyle}>Processing Workers Automatic</caption>
				<tbody>
					{Object.entries(data?.data.workers?.["processing-workers-auto"] || {}).map(([node, workers]) => (
						<tr key={node}>
							<td style={tdStyle}>
								<table style={nestedTableStyle}>
									<caption style={captionStyle}>{node}</caption>
									<thead>
										<tr>
											<th style={thStyle}>ID</th>
											<th style={thStyle}>Status</th>
											<th style={thStyle}>Name</th>
											<th style={thStyle}>Last Update</th>
										</tr>
									</thead>
									<tbody>
										{workers.sort(workerSort).map((worker) => (
											<tr key={worker.id}>
												<td style={tdStyle}>{worker.id}</td>
												<td style={tdStyle}>{worker.status}</td>
												<td style={tdStyle}>
													{worker.status === "busy" ? (
														<LinkToVideo worker={worker} />
													) : (
														""
													)}
												</td>
												<td style={tdStyle}>{new Date(worker.lastUpdate).toLocaleString()}</td>
											</tr>
										))}
									</tbody>
								</table>
							</td>
						</tr>
					))}
				</tbody>
			</table>

			<table style={tableStyle}>
				<caption style={captionStyle}>Backup Workers</caption>
				<thead>
					<tr>
						<td style={thStyle}>Id</td>
						<td style={thStyle}>Status</td>
						<td style={thStyle}>File</td>
						<td style={thStyle}>Video Id</td>
						<td style={thStyle}>Last Update</td>
					</tr>
				</thead>
				<tbody>
					{data?.data.workers?.["backup-workers"]?.sort(workerSort).map((worker) => {
						return (
							<tr key={worker.id}>
								<td style={tdStyle}>{worker.id}</td>
								<td style={tdStyle}>{worker.status}</td>
								<td style={tdStyle}>{worker.status === "busy" ? `${worker.name}(${worker.file})` : ""}</td>
								<td style={tdStyle}>{worker.status === "busy" ? worker.videoId : ""}</td>
								<td style={tdStyle}>{new Date(worker.lastUpdate).toLocaleString()}</td>
							</tr>
						);
					})}
				</tbody>
			</table>

			<table style={tableStyle}>
				<caption style={captionStyle}>Ingest Workers</caption>
				<thead>
					<tr>
						<td style={thStyle}>Id</td>
						<td style={thStyle}>Status</td>
						<td style={thStyle}>File</td>
						<td style={thStyle}>Uploaded By</td>
						<td style={thStyle}>Last Update</td>
					</tr>
				</thead>
				<tbody>
					{data?.data.workers?.["ingest-workers"]?.sort(workerSort).map((worker) => {
						return (
							<tr key={worker.id}>
								<td style={tdStyle}>{worker.id}</td>
								<td style={tdStyle}>{worker.status}</td>
								<td style={tdStyle}>{worker.status === "busy" ? `${worker.name}(${worker.file})` : ""}</td>
								<td style={tdStyle}>{worker.status === "busy" ? worker.uploadedBy : ""}</td>
								<td style={tdStyle}>{new Date(worker.lastUpdate).toLocaleString()}</td>
							</tr>
						);
					})}
				</tbody>
			</table>

			<table style={tableStyle}>
				<caption style={captionStyle}>Upload Workers</caption>
				<thead>
					<tr>
						<td style={thStyle}>Id</td>
						<td style={thStyle}>Status</td>
						<td style={thStyle}>Name</td>
						<td style={thStyle}>Video Id</td>
						<td style={thStyle}>Last Update</td>
					</tr>
				</thead>
				<tbody>
					{data?.data.workers?.["upload-workers"]?.sort(workerSort).map((worker) => {
						return (
							<tr key={worker.id}>
								<td style={tdStyle}>{worker.id}</td>
								<td style={tdStyle}>{worker.status}</td>
								<td style={tdStyle}>{worker.status === "busy" ? <LinkToVideo worker={worker} /> : ""}</td>
								<td style={tdStyle}>{worker.status === "busy" ? worker.videoId : ""}</td>
								<td style={tdStyle}>{new Date(worker.lastUpdate).toLocaleString()}</td>
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
}
